import React from 'react'
import { clsx } from 'clsx'
import { CertificationInlineRich } from './CertificationRichText'

function StemText({ text }) {
  if (!text) return null
  return (
    <div className="text-body text-neutral-100 leading-relaxed whitespace-pre-wrap">
      <CertificationInlineRich text={text} />
    </div>
  )
}

export default function QuestionCard({ question, value, onChange, questionNumber }) {
  const tierLabel = question.tier === 'core' ? 'Core (1–20)' : 'Advanced (21–40)'
  const pts = question.points ?? (question.id <= 20 ? 2 : 3)
  const ptsLabel = `${pts} pts if correct`

  if (question.type === 'tf') {
    const v = typeof value === 'boolean' ? value : null
    return (
      <div className="glass-effect-dark rounded-2xl p-6 sm:p-8 border border-primary-500/15 space-y-6">
        <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wider text-primary-400 font-semibold">
          <span>True / False</span>
          <span className="text-neutral-600">·</span>
          <span className="text-neutral-400">{tierLabel}</span>
          <span className="text-neutral-600">·</span>
          <span className="text-primary-300">{ptsLabel}</span>
        </div>
        <StemText text={question.stem} />
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => onChange(true)}
            className={clsx(
              'flex-1 rounded-xl px-6 py-4 text-sm font-semibold border transition-all duration-200',
              v === true
                ? 'bg-primary-500/20 border-primary-500 text-white ring-2 ring-primary-500/40'
                : 'bg-white/5 border-white/10 text-neutral-200 hover:bg-white/10 hover:border-white/20'
            )}
          >
            True
          </button>
          <button
            type="button"
            onClick={() => onChange(false)}
            className={clsx(
              'flex-1 rounded-xl px-6 py-4 text-sm font-semibold border transition-all duration-200',
              v === false
                ? 'bg-primary-500/20 border-primary-500 text-white ring-2 ring-primary-500/40'
                : 'bg-white/5 border-white/10 text-neutral-200 hover:bg-white/10 hover:border-white/20'
            )}
          >
            False
          </button>
        </div>
      </div>
    )
  }

  if (question.type === 'mc') {
    const selected = typeof value === 'string' ? value.toUpperCase() : null
    return (
      <div className="glass-effect-dark rounded-2xl p-6 sm:p-8 border border-primary-500/15 space-y-6">
        <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wider text-primary-400 font-semibold">
          <span>Multiple choice</span>
          <span className="text-neutral-600">·</span>
          <span className="text-neutral-400">{tierLabel}</span>
          <span className="text-neutral-600">·</span>
          <span className="text-primary-300">{ptsLabel}</span>
        </div>
        <StemText text={question.stem} />
        <fieldset className="space-y-3">
          <legend className="sr-only">Question {questionNumber} options</legend>
          {question.options.map((opt) => {
            const id = `q${question.id}-opt-${opt.key}`
            return (
              <label
                key={opt.key}
                htmlFor={id}
                className={clsx(
                  'flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-all duration-200',
                  selected === opt.key
                    ? 'border-primary-500 bg-primary-500/10 ring-2 ring-primary-500/30'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                )}
              >
                <input
                  id={id}
                  type="radio"
                  name={`mc-${question.id}`}
                  checked={selected === opt.key}
                  onChange={() => onChange(opt.key)}
                  className="mt-1 h-4 w-4 border-neutral-600 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-100 leading-relaxed">
                  <span className="font-semibold text-primary-400">{opt.key}.</span>{' '}
                  <CertificationInlineRich text={opt.text} />
                </span>
              </label>
            )
          })}
        </fieldset>
      </div>
    )
  }

  // MS — opt.text might contain ** ; render in label
  const selected = Array.isArray(value) ? value.map((x) => String(x).toUpperCase()) : []
  const toggle = (key) => {
    const k = key.toUpperCase()
    if (selected.includes(k)) onChange(selected.filter((x) => x !== k))
    else onChange([...selected, k])
  }

  return (
    <div className="glass-effect-dark rounded-2xl p-6 sm:p-8 border border-primary-500/15 space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wider text-primary-400 font-semibold">
        <span>Multi-select</span>
        <span className="text-neutral-600">·</span>
        <span className="text-neutral-400">{tierLabel}</span>
        <span className="text-neutral-600">·</span>
        <span className="text-primary-300">{ptsLabel}</span>
      </div>
      <StemText text={question.stem} />
      <p className="text-sm text-neutral-400">Select all that apply.</p>
      <fieldset className="space-y-3">
        <legend className="sr-only">Question {questionNumber} multi-select</legend>
        {question.options.map((opt) => {
          const id = `q${question.id}-ms-${opt.key}`
          const checked = selected.includes(opt.key)
          return (
            <label
              key={opt.key}
              htmlFor={id}
              className={clsx(
                'flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-all duration-200',
                checked
                  ? 'border-primary-500 bg-primary-500/10 ring-2 ring-primary-500/30'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              )}
            >
              <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.key)}
                className="mt-1 h-4 w-4 rounded border-neutral-600 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-100 leading-relaxed">
                <span className="font-semibold text-primary-400">{opt.key}.</span>{' '}
                <CertificationInlineRich text={opt.text} />
              </span>
            </label>
          )
        })}
      </fieldset>
    </div>
  )
}
